import { useState, type ReactNode } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import {
  ArrowLeft,
  FolderOpen,
  Plus,
  Phone,
  Mail,
  CalendarDays,
  CheckCircle2,
  Syringe,
  Pill,
  ClipboardList,
  Stethoscope,
  AlertTriangle,
  Smile,
  Activity,
  Droplets,
  Utensils,
  Weight,
  Printer,
  Home,
  FileText,
} from "lucide-react";
import "./NazorgDetail.css";

/* ── Wat het scherm van buiten krijgt ─────────────────────────────────────────
   Dit is het nazorg-detailscherm zoals het was (frontend-react/nazorg-detail), teruggezet in de SPA.
   Alleen de brug is anders: de gegevens komen nu van de api in plaats van via postMessage van de
   Jmix-host, en "terug" en "dossier openen" zijn callbacks. */
export interface NazorgDetailProps {
  /** De inzending die je aanklikte in het nazorg-overzicht. */
  inzendingId: string;
  /** Terug naar het overzicht. */
  onTerug: () => void;
  /** Het dierdossier openen — een knop, geen bestemming waar je ongevraagd belandt. */
  onOpenDier: (dierId: string) => void;
}

/** Wat de api levert; de veldnamen zijn die van de oude payload. */
interface NazorgDetailData {
  inzendingId: number;
  dierId: number | null;
  prioriteit: string | null;
  opgepakt: boolean;
  contactmomentLabel: string | null;
  ingediendOp: string | null;
  geleden: string | null;
  geflagdSnippet: string | null;
  signalen: number;
  dier: {
    dierId: number | null;
    naam: string | null;
    diersoort: string | null;
    ras: string | null;
    geslacht: string | null;
    gecastreerd: boolean;
    geboortedatum: string | null;
    leeftijd: string | null;
    chipnummer: string | null;
    interneId: string | null;
    bijzonderheden: string | null;
    fotoUrl: string | null;
  } | null;
  adoptant: { klantId: number | null; naam: string | null; email: string | null; telefoon: string | null } | null;
  antwoorden: { fieldKey: string; sectie: string | null; vraag: string | null; antwoord: string | null; kleur: string | null; signaal: boolean }[];
  scores: { groen: number; oranje: number; rood: number; totaal: number };
  gewicht: {
    laatsteKg: number | null;
    laatsteDatum: string | null;
    deltaKg: number | null;
    deltaSinds: string | null;
    historie: GewichtPunt[];
  } | null;
  entingen: { naam: string | null; datum: string | null; volgende: string | null }[];
  entingenOpOrde: boolean;
  medicijnen: { naam: string | null; dosering: string | null; frequentie: string | null }[];
  notities: { datum: string | null; melder: string | null; tekst: string | null }[];
  taken: {
    id: string | null;
    /** VRAGENLIJST, BELAFSPRAAK, HUISBEZOEK of TAAK — bepaalt of er een verslag bij hoort. */
    soort: string | null;
    titel: string | null;
    omschrijving: string | null;
    datum: string | null;
    status: string | null;
  }[];
  notitieTags: { id: number; label: string | null; kleur: string | null }[];
  /** De vragenlijsten die naar dit dier kunnen: voor zijn diersoort, of voor alle soorten. */
  vragenlijsten: { id: string; naam: string | null; contactmoment: string | null; contactmomentLabel: string | null }[];
  /** Of er nog een vragenlijst naar de adoptant mag; een dier dat terug is heeft er geen. */
  magVersturen: boolean;
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */
const KLEUR_UI: Record<string, { color: string; label: string }> = {
  rood: { color: "red", label: "Rood" },
  oranje: { color: "orange", label: "Oranje" },
  groen: { color: "green", label: "Groen" },
};
function kleurUi(kleur: string | null | undefined) {
  return (kleur ? KLEUR_UI[kleur] : null) || KLEUR_UI.groen;
}

/**
 * De score bij één antwoord. Zonder kleur weten we het niet — dan staat er "Onbekend" en geen groen:
 * groen zetten op een antwoord dat nooit is beoordeeld leest als "alles oké" terwijl de inzending als
 * oranje binnenkwam.
 */
function scoreUi(kleur: string | null | undefined) {
  return kleur && KLEUR_UI[kleur] ? KLEUR_UI[kleur] : { color: "grey", label: "Onbekend" };
}

/** Icoon-heuristiek per vraagtekst — dezelfde beeldtaal als de mockup. */
function vraagIcoon(vraag: string | null | undefined) {
  const q = (vraag || "").toLowerCase();
  if (/(voelt|welzijn|blij)/.test(q)) return Smile;
  if (/(gewenning|went|thuis)/.test(q)) return Activity;
  if (/(eet|voeding|eetlust)/.test(q)) return Utensils;
  if (/(ontlasting|drink|plas|zindelijk)/.test(q)) return Droplets;
  if (/(dierenarts|controle|gezondheid)/.test(q)) return Stethoscope;
  if (/(medicijn|medicatie)/.test(q)) return Pill;
  if (/(probleem|gedrag|blaf|angst)/.test(q)) return AlertTriangle;
  if (/(gewicht|weeg)/.test(q)) return Weight;
  return ClipboardList;
}

function kg(value: number | null | undefined) {
  return value == null ? "—" : String(value).replace(".", ",") + " kg";
}

interface GewichtPunt {
  datum: string;
  kg: number | null;
}

/** Echte lijngrafiek (SVG) van de gewichtshistorie: vlak + lijn + punten + datum/kg-labels. */
function GewichtGrafiek({ historie }: { historie: GewichtPunt[] }) {
  const W = 280;
  const H = 120;
  const PAD = { l: 34, r: 12, t: 12, b: 22 };
  const punten = historie.filter((h): h is { datum: string; kg: number } => typeof h.kg === "number");
  if (punten.length === 0) return null;

  const kgs = punten.map((p) => p.kg);
  const min = Math.min(...kgs);
  const max = Math.max(...kgs);
  const marge = Math.max((max - min) * 0.25, 0.5);
  const yMin = min - marge;
  const yMax = max + marge;
  const x = (i: number) =>
    punten.length === 1
      ? PAD.l + (W - PAD.l - PAD.r) / 2
      : PAD.l + (i / (punten.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - yMin) / (yMax - yMin)) * (H - PAD.t - PAD.b);

  const lijn = punten.map((p, i) => `${x(i)},${y(p.kg)}`).join(" ");
  const vlak = `${PAD.l},${H - PAD.b} ${lijn} ${x(punten.length - 1)},${H - PAD.b}`;
  // Max 4 datum-labels, gelijkmatig verdeeld (altijd eerste + laatste).
  const stap = Math.max(1, Math.ceil(punten.length / 4));
  const labelIdx = punten.map((_, i: number) => i).filter(
    (i: number) => i === 0 || i === punten.length - 1 || i % stap === 0,
  );

  return (
    <svg
      className="gewicht-grafiek"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Gewichtshistorie"
    >
      {/* horizontale hulplijnen + kg-labels */}
      {[yMax, (yMax + yMin) / 2, yMin].map((v, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={y(v)} x2={W - PAD.r} y2={y(v)} className="gg-grid" />
          <text x={PAD.l - 5} y={y(v) + 3} className="gg-ylabel">
            {v.toFixed(1).replace(".", ",")}
          </text>
        </g>
      ))}
      {punten.length > 1 && <polygon points={vlak} className="gg-vlak" />}
      {punten.length > 1 && <polyline points={lijn} className="gg-lijn" />}
      {punten.map((p, i) => (
        <circle key={i} cx={x(i)} cy={y(p.kg)} r="3.4" className="gg-punt">
          <title>{`${p.datum}: ${kg(p.kg)}`}</title>
        </circle>
      ))}
      {labelIdx.map((i) => (
        <text key={i} x={x(i)} y={H - 6} className="gg-xlabel">
          {punten[i].datum}
        </text>
      ))}
    </svg>
  );
}

/* ── Hoofdcomponent ───────────────────────────────────────────────────────── */
/** Wat er vanaf het dossier ingepland kan worden. */
type PlanSoort = "VRAGENLIJST" | "BELAFSPRAAK" | "HUISBEZOEK";

const PLAN_SOORTEN: { sleutel: PlanSoort; label: string; icoon: Icoon }[] = [
  { sleutel: "VRAGENLIJST", label: "Vragenlijst", icoon: ClipboardList },
  { sleutel: "BELAFSPRAAK", label: "Belafspraak", icoon: Phone },
  { sleutel: "HUISBEZOEK", label: "Huisbezoek", icoon: Home },
];

/** Van een belafspraak en een huisbezoek wordt een verslag verwacht; van de rest niet. */
function vraagtVerslag(soort: string | null | undefined) {
  return soort === "BELAFSPRAAK" || soort === "HUISBEZOEK";
}

/** yyyy-MM-dd → dd-MM-yyyy, voor de bevestiging na het inplannen. */
function datumKort(datum: string) {
  const [jaar, maand, dag] = datum.split("-");
  return dag && maand && jaar ? `${dag}-${maand}-${jaar}` : datum;
}

/** Datum over 14 dagen (yyyy-MM-dd) — standaard voor het nieuwe nazorg-moment. */
function overTweeWeken() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

export function NazorgDetail({ inzendingId, onTerug, onOpenDier }: NazorgDetailProps) {
  const { data: antwoord, refetch } = useCustom<NazorgDetailData>({
    url: `nazorg/beheer/inzendingen/${inzendingId}`,
    method: "get",
  });
  const { mutate } = useCustomMutation();
  const data = antwoord?.data ?? null;
  const [planDatum, setPlanDatum] = useState(overTweeWeken);
  // Wat er ingepland wordt: een volgende vragenlijst, een belafspraak of een huisbezoek.
  const [planSoort, setPlanSoort] = useState<PlanSoort>("VRAGENLIJST");
  const [planLijst, setPlanLijst] = useState("");
  const [planOpmerking, setPlanOpmerking] = useState("");
  /** De taak waarvan het verslag geschreven wordt; die wordt na het opslaan afgerond. */
  const [verslagTaak, setVerslagTaak] = useState<{ id: string; soort: string } | null>(null);
  /** Wat het inplannen opleverde: een bevestiging, of waarom het niet ging. */
  const [planMelding, setPlanMelding] = useState<{ fout: boolean; tekst: string } | null>(null);
  const [notitieOpen, setNotitieOpen] = useState(false);
  const [infoDialog, setInfoDialog] = useState<string | null>(null); // 'entingen' | 'notities'
  const [notitieTitel, setNotitieTitel] = useState("");
  const [notitieTekst, setNotitieTekst] = useState("");
  const [notitieDatum, setNotitieDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [notitieOpvolg, setNotitieOpvolg] = useState("");
  const [notitieAuteur, setNotitieAuteur] = useState("");
  const [notitieVast, setNotitieVast] = useState(false);
  const [notitieTags, setNotitieTags] = useState<number[]>([]);

  // Haken horen boven elke vroege return: anders draait dit scherm bij het laden minder haken dan
  // daarna. fotoUrl is de ONLINE afbeelding (die van de advertentie) en blijft leeg voor dieren die
  // het asiel zelf fotografeerde; het dieroverzicht haalt daarom de geüploade foto op achter de login.
  const fotoDierId = data?.dier?.dierId ?? null;
  const dierFotos = useAnimalPhotoUrls(fotoDierId != null ? [fotoDierId] : []);

  if (!data) {
    return (
      <section className="content">
        <div className="page-actions">
          <button className="back-button" onClick={onTerug}>
            <ArrowLeft size={18} /> Terug naar overzicht
          </button>
        </div>
        <div className="panel" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          Nazorg-resultaten laden…
        </div>
      </section>
    );
  }

  const dier = data.dier ?? ({} as NonNullable<NazorgDetailData["dier"]>);
  const adoptant = data.adoptant ?? ({} as NonNullable<NazorgDetailData["adoptant"]>);
  const antwoorden = data.antwoorden || [];
  const scores = data.scores || { groen: 0, oranje: 0, rood: 0, totaal: antwoorden.length };
  const gewicht = data.gewicht;
  const entingen = data.entingen || [];
  const medicijnen = data.medicijnen || [];
  const notities = data.notities || [];
  const taken = data.taken || [];
  const vragenlijsten = data.vragenlijsten || [];
  // De bovenste lijst is er één voor deze diersoort (zo geeft de api ze terug), en die staat voor:
  // je stuurt de lijst die voor dit dier gemaakt is, tenzij je zelf iets anders kiest.
  const gekozenLijst = planLijst || vragenlijsten[0]?.id || "auto";
  const prio = kleurUi(data.prioriteit);
  const dierId = dier.dierId;
  const dierFoto = (dierId != null ? dierFotos[String(dierId)] : null) ?? dier.fotoUrl;

  const naarDossier = () => {
    if (dierId != null) onOpenDier(String(dierId));
  };
  const sluitNazorg = () => {
    if (!window.confirm("Nazorg sluiten? De inzending verdwijnt dan uit het overzicht.")) return;
    mutate(
      {
        url: `nazorg/beheer/inzendingen/${data.inzendingId}/oppakken`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => refetch() },
    );
  };
  /**
   * Een volgend nazorgmoment: een vragenlijst op een datum, een belafspraak of een huisbezoek. Alle
   * drie worden het opvolgtaken bij het dier; de vragenlijst gaat op die datum vanzelf de deur uit.
   */
  const planMoment = () => {
    if (dierId == null || !planDatum) return;
    setPlanMelding(null);
    mutate(
      {
        url: `animals/${dierId}/nazorg/moment`,
        method: "post",
        values: {
          soort: planSoort,
          datum: planDatum,
          vragenlijstId:
            planSoort === "VRAGENLIJST" && gekozenLijst !== "auto" ? gekozenLijst : null,
          opmerking: planSoort === "VRAGENLIJST" ? null : planOpmerking.trim() || null,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setPlanOpmerking("");
          setPlanMelding({ fout: false, tekst: `Ingepland op ${datumKort(planDatum)}.` });
          refetch();
        },
        // Zonder dit zou een geweigerde planning niets doen en niets zeggen.
        onError: (fout) =>
          setPlanMelding({
            fout: true,
            tekst:
              (fout as { response?: { data?: { message?: string } }; message?: string })?.response?.data
                ?.message
              || (fout as { message?: string })?.message
              || "Inplannen lukte niet.",
          }),
      },
    );
  };
  /** Het verslag van een belafspraak of huisbezoek: het notitievenster, met de taak eraan vast. */
  const schrijfVerslag = (taak: { id: string | null; soort: string | null; titel: string | null }) => {
    if (!taak.id) return;
    setVerslagTaak({ id: taak.id, soort: taak.soort ?? "TAAK" });
    setNotitieTitel(taak.soort === "HUISBEZOEK" ? "Verslag huisbezoek" : "Verslag belafspraak");
    setNotitieTekst("");
    setNotitieDatum(new Date().toISOString().slice(0, 10));
    setNotitieOpen(true);
  };
  const opentNotitie = () => {
    setVerslagTaak(null);
    setNotitieTitel("");
    setNotitieDatum(new Date().toISOString().slice(0, 10));
    setNotitieOpen(true);
  };
  const wisselTag = (id: number) =>
    setNotitieTags((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  /** De notitie komt op het dier terecht, op dezelfde plek als de notities op het dierscherm. */
  const notitieOpslaan = () => {
    if (!notitieTekst.trim() || dierId == null) return;
    mutate(
      {
        url: `animals/${dierId}/notes`,
        method: "post",
        values: {
          title: notitieTitel.trim() || notitieTekst.trim().slice(0, 60),
          date: notitieDatum,
          note: notitieTekst.trim(),
          author: notitieAuteur.trim() || "Nazorg",
          followUpDate: notitieOpvolg || null,
          pinned: notitieVast,
          tagIds: notitieTags,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          // Was het een verslag, dan is de afspraak daarmee gedaan: de taak gaat dicht.
          if (verslagTaak) {
            mutate(
              {
                url: `taken/${verslagTaak.id}/status`,
                method: "post",
                values: { status: "AFGEROND" },
                successNotification: false,
                errorNotification: false,
              },
              { onSuccess: () => refetch() },
            );
          }
          refetch();
        },
      },
    );
    setNotitieOpen(false);
    setVerslagTaak(null);
    setNotitieTitel("");
    setNotitieTekst("");
    setNotitieOpvolg("");
    setNotitieVast(false);
    setNotitieTags([]);
  };

  const meta = [dier.diersoort, dier.ras, dier.geslacht, dier.gecastreerd ? "Gecastreerd" : null]
    .filter(Boolean)
    .join(" · ");

  /* score-ring: conic-gradient uit de tellingen */
  const tot = Math.max(scores.totaal || 0, 1);
  const pGroen = (scores.groen / tot) * 360;
  const pOranje = (scores.oranje / tot) * 360;
  const ringStyle = {
    background: `conic-gradient(var(--green) 0deg ${pGroen}deg, var(--orange) ${pGroen}deg ${
      pGroen + pOranje
    }deg, var(--red) ${pGroen + pOranje}deg 360deg)`,
  };

  const historie = gewicht?.historie || [];

  return (
    <section className="content">
      <div className="page-actions">
        <button className="back-button" onClick={onTerug}>
          <ArrowLeft size={18} /> Terug naar overzicht
        </button>
        <div className="action-buttons">
          <button className="outline-button" onClick={naarDossier}>
            <FolderOpen size={17} /> Dossier openen
          </button>
          <button className="outline-button sluiten-button" onClick={sluitNazorg}>
            <CheckCircle2 size={17} /> Nazorg sluiten
          </button>
        </div>
      </div>

      {/* ── Kop: dier + gezondheidskaarten + adoptant ─────────────────────── */}
      <section className="animal-header">
        {dierFoto ? (
          <img
            src={dierFoto}
            alt={dier.naam ?? ""}
            className="animal-photo"
            onError={(e) => {
              e.currentTarget.outerHTML = '<div class="animal-photo animal-photo--leeg">🐾</div>';
            }}
          />
        ) : (
          <div className="animal-photo animal-photo--leeg">🐾</div>
        )}

        <div className="animal-info">
          <div className="animal-title-row">
            <h1>{dier.naam || "—"}</h1>
            <span className={`status-pill ${prio.color}`}>{prio.label} aandacht</span>
          </div>

          {meta && <div className="animal-meta">{meta}</div>}

          <dl className="animal-details">
            {dier.geboortedatum && (
              <div>
                <dt>Geboortedatum</dt>
                <dd>
                  {dier.geboortedatum}
                  {dier.leeftijd ? ` · ${dier.leeftijd}` : ""}
                </dd>
              </div>
            )}
            {dier.chipnummer && (
              <div>
                <dt>Chipnummer</dt>
                <dd>{dier.chipnummer}</dd>
              </div>
            )}
            {dier.interneId && (
              <div>
                <dt>Interne ID</dt>
                <dd>{dier.interneId}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="health-cards">
          <InfoCard
            icon={Weight}
            label="Gewicht"
            value={gewicht ? kg(gewicht.laatsteKg) : "—"}
            sub={
              gewicht && gewicht.deltaKg != null
                ? `${gewicht.deltaKg > 0 ? "+" : ""}${String(gewicht.deltaKg.toFixed(1)).replace(".", ",")} kg vs ${gewicht.deltaSinds}`
                : "Geen eerdere meting"
            }
            tone={gewicht && gewicht.deltaKg != null && gewicht.deltaKg < 0 ? "green" : ""}
          />
          <InfoCard
            icon={Syringe}
            label="Entingen"
            value={entingen.length === 0 ? "—" : data.entingenOpOrde ? "Op orde" : "Aandacht"}
            sub={entingen.length === 0 ? "Geen registraties" : data.entingenOpOrde ? "Volledig volgens schema" : "Herhaling verlopen"}
            tone={entingen.length === 0 ? "" : data.entingenOpOrde ? "green" : "orange"}
          />
          <InfoCard
            icon={Pill}
            label="Medicijnen"
            value={medicijnen.length === 0 ? "Geen" : `${medicijnen.length} actief`}
            sub={medicijnen.length === 0 ? "Geen lopende voorschriften" : "Bekijk overzicht"}
            tone={medicijnen.length > 0 ? "orange" : ""}
          />
          <InfoCard
            icon={CalendarDays}
            label="Laatste vaccinatie"
            value={entingen[0]?.datum || "—"}
            sub={entingen[0]?.naam || "Geen registraties"}
          />
        </div>

        <div className="adoptant-card">
          <span>Adoptant</span>
          <strong>{adoptant.naam || "Onbekend"}</strong>
          {adoptant.telefoon && (
            <p>
              <Phone size={15} /> <a href={`tel:${adoptant.telefoon}`}>{adoptant.telefoon}</a>
            </p>
          )}
          {adoptant.email && (
            <p>
              <Mail size={15} /> <a href={`mailto:${adoptant.email}`}>{adoptant.email}</a>
            </p>
          )}
        </div>
      </section>

      {/* ── Drie panelen ──────────────────────────────────────────────────── */}
      <section className="dashboard-grid">
        {/* 1 — Beoordeling per vraag */}
        <div className="panel answers-panel">
          <PanelHeader
            number="1"
            title="Beoordeling per vraag"
            action={
              <button className="icon-button" onClick={() => window.print()} title="Nazorg printen">
                <Printer size={18} />
              </button>
            }
          />
          <p className="panel-subtitle">
            Nazorg ingevuld{data.ingediendOp ? ` op ${data.ingediendOp}` : ""}
            {data.contactmomentLabel ? ` · ${data.contactmomentLabel}` : ""}
          </p>

          <div className="answer-table-header">
            <span>Vraag</span>
            <span>Antwoord</span>
            <span>Score</span>
          </div>

          <div className="answer-list">
            {antwoorden.length === 0 ? (
              <p className="small-text">Geen antwoorden bij deze inzending.</p>
            ) : (
              antwoorden.map((a, i) => {
                const ui = scoreUi(a.kleur);
                return (
                  <AnswerRow
                    key={i}
                    icon={vraagIcoon(a.vraag)}
                    question={a.vraag}
                    answer={a.antwoord || "—"}
                    score={ui.label}
                    color={ui.color}
                  />
                );
              })
            )}
          </div>

          <div className="legend">
            <span>
              <i className="dot green" /> Groen = alles oké
            </span>
            <span>
              <i className="dot orange" /> Oranje = aandacht
            </span>
            <span>
              <i className="dot red" /> Rood = actie nodig
            </span>
          </div>
        </div>

        {/* 2 — Medisch & dierinformatie */}
        <div className="panel medical-panel">
          <PanelHeader number="2" title="Medisch & dierinformatie" />

          <MedicalBlock title="Gewichtshistorie">
            {gewicht ? (
              <>
                <div className="weight-box">
                  <GewichtGrafiek historie={historie} />
                  <div>
                    <strong>{kg(gewicht.laatsteKg)}</strong>
                    {gewicht.deltaKg != null && (
                      <p className={gewicht.deltaKg <= 0 ? "positive" : ""}>
                        {gewicht.deltaKg > 0 ? "+" : ""}
                        {String(gewicht.deltaKg.toFixed(1)).replace(".", ",")} kg
                      </p>
                    )}
                    {gewicht.deltaSinds && <small>vs {gewicht.deltaSinds}</small>}
                  </div>
                </div>
                <button className="text-link" onClick={naarDossier}>
                  Bekijk alle metingen
                </button>
              </>
            ) : (
              <p className="small-text">Nog geen gewichtsmetingen geregistreerd.</p>
            )}
          </MedicalBlock>

          <MedicalBlock
            title="Entingen"
            badge={
              entingen.length > 0 ? (
                <span className={`status-pill ${data.entingenOpOrde ? "green" : "orange"}`}>
                  {data.entingenOpOrde ? "Op orde" : "Aandacht"}
                </span>
              ) : null
            }
          >
            {entingen.length === 0 ? (
              <p className="small-text">Geen vaccinaties geregistreerd.</p>
            ) : (
              <>
                <div className="data-lines">
                  {entingen.slice(0, 4).map((e, i) => (
                    <DataLine key={i} label={e.naam} date={e.datum} next={e.volgende} />
                  ))}
                </div>
                <button className="text-link" onClick={() => setInfoDialog("entingen")}>
                  Bekijk vaccinatieoverzicht
                </button>
              </>
            )}
          </MedicalBlock>

          <MedicalBlock
            title="Medicijnen"
            badge={
              medicijnen.length > 0 ? (
                <span className="status-pill orange">{medicijnen.length} actief</span>
              ) : null
            }
          >
            {medicijnen.length === 0 ? (
              <p className="small-text">Geen lopende voorschriften.</p>
            ) : (
              <>
                <div className="medicine-list">
                  {medicijnen.map((m, i) => (
                    <Medicine
                      key={i}
                      name={m.naam}
                      purpose={m.dosering || ""}
                      dose={m.frequentie || ""}
                    />
                  ))}
                </div>
                <button className="text-link" onClick={naarDossier}>
                  Bekijk medicatieoverzicht
                </button>
              </>
            )}
          </MedicalBlock>

          <MedicalBlock title="Allergieën & bijzonderheden">
            {dier.bijzonderheden ? (
              <p className="small-text">{dier.bijzonderheden}</p>
            ) : (
              <p className="small-text">Geen bekende bijzonderheden.</p>
            )}
          </MedicalBlock>
        </div>

        {/* 3 — Notities & opvolging */}
        <div className="panel followup-panel">
          <PanelHeader number="3" title="Notities & opvolging" />

          <div className="followup-layout">
            <div className="notes-card">
              <h3>Recente notities</h3>
              {notities.length === 0 ? (
                <p className="small-text">Nog geen notities.</p>
              ) : (
                notities.slice(0, 3).map((nt, i) => (
                  <TimelineNote
                    key={i}
                    color={i === 0 ? "orange" : "green"}
                    date={nt.datum || ""}
                    user={nt.melder || ""}
                    text={nt.tekst || ""}
                  />
                ))
              )}
              <button className="text-link" onClick={() => setInfoDialog("notities")}>
                Bekijk alle notities
              </button>
            </div>

            <div className="score-card">
              <h3>Overzicht scores</h3>
              <div className="score-ring" style={ringStyle}>
                <div>
                  <strong>{scores.totaal}</strong>
                  <span>totaal</span>
                </div>
              </div>
              <ul className="score-list">
                <li>
                  <i className="dot green" /> <strong>{scores.groen}</strong> Groen
                </li>
                <li>
                  <i className="dot orange" /> <strong>{scores.oranje}</strong> Oranje
                </li>
                <li>
                  <i className="dot red" /> <strong>{scores.rood}</strong> Rood
                </li>
              </ul>
            </div>
          </div>

          <div className="tasks-card">
            <h3>Opvolgacties</h3>
            {taken.length === 0 ? (
              <p className="small-text">Geen open opvolgtaken.</p>
            ) : (
              taken.map((t, i) => (
                <Task
                  key={t.id ?? i}
                  icon={
                    t.soort === "BELAFSPRAAK"
                      ? Phone
                      : t.soort === "HUISBEZOEK"
                        ? Home
                        : /dierenarts|controle/i.test(t.titel || "")
                          ? Stethoscope
                          : ClipboardList
                  }
                  title={t.titel || "—"}
                  sub={t.omschrijving || ""}
                  date={t.datum || ""}
                  status={t.status || "Gepland"}
                  color={t.status === "Te doen" ? "orange" : "green"}
                  // Een belafspraak en een huisbezoek zijn pas klaar als er een verslag van is.
                  actie={
                    vraagtVerslag(t.soort) && t.id ? (
                      <button className="task-row__verslag" onClick={() => schrijfVerslag(t)}>
                        <FileText size={14} /> Verslag
                      </button>
                    ) : null
                  }
                />
              ))
            )}
            <button className="text-link arrow-link" onClick={naarDossier}>
              Alle taken bekijken →
            </button>
          </div>

          <div className="plan-moment">
            <h3>Nieuw nazorg-moment</h3>
            <div className="plan-moment__keuze" role="group" aria-label="Wat plannen we in?">
              {PLAN_SOORTEN.map(({ sleutel, label, icoon: Icon }) => (
                <button
                  key={sleutel}
                  type="button"
                  className={`plan-moment__soort${planSoort === sleutel ? " is-actief" : ""}`}
                  aria-pressed={planSoort === sleutel}
                  onClick={() => setPlanSoort(sleutel)}
                >
                  <Icon size={15} /> {label}
                </button>
              ))}
            </div>
            <p className="small-text">
              {planSoort === "VRAGENLIJST"
                ? "Op de gekozen datum gaat de vragenlijst vanzelf naar de adoptant."
                : planSoort === "BELAFSPRAAK"
                  ? "Er komt een opvolgtaak om te bellen; het verslag schrijf je er daarna bij."
                  : "Er komt een opvolgtaak voor het huisbezoek; het verslag schrijf je er daarna bij."}
            </p>
            {planSoort === "VRAGENLIJST" ? (
              <label className="plan-moment__veld">
                <span>Vragenlijst{dier.diersoort ? ` voor ${dier.diersoort.toLowerCase()}` : ""}</span>
                <select value={gekozenLijst} onChange={(e) => setPlanLijst(e.target.value)}>
                  {vragenlijsten.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.naam}
                      {v.contactmomentLabel ? ` — ${v.contactmomentLabel}` : ""}
                    </option>
                  ))}
                  {/* Laatste keus: dan bepaalt de uitgifte zelf welke lijst op dat moment past. */}
                  <option value="auto">Automatisch kiezen</option>
                </select>
              </label>
            ) : (
              <label className="plan-moment__veld">
                <span>Waar gaat het over?</span>
                <input
                  type="text"
                  value={planOpmerking}
                  placeholder={planSoort === "BELAFSPRAAK" ? "Bijv. navragen of het eten beter gaat" : "Bijv. kennismaken met de nieuwe situatie"}
                  onChange={(e) => setPlanOpmerking(e.target.value)}
                />
              </label>
            )}
            <div className="plan-moment__row">
              <input
                type="date"
                value={planDatum}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setPlanDatum(e.target.value)}
              />
              <button
                className="outline-button"
                onClick={planMoment}
                disabled={!planDatum || (planSoort === "VRAGENLIJST" && !data.magVersturen)}
              >
                <CalendarDays size={17} /> Inplannen
              </button>
            </div>
            {planSoort === "VRAGENLIJST" && !data.magVersturen && (
              <p className="plan-moment__melding is-fout">
                Dit dier is niet meer geplaatst — er is dus geen adoptant om een vragenlijst naar te
                sturen. Een belafspraak of huisbezoek inplannen kan wel.
              </p>
            )}
            {planSoort === "VRAGENLIJST" && data.magVersturen && vragenlijsten.length === 0 && (
              <p className="small-text">
                Er is nog geen gepubliceerde vragenlijst voor deze diersoort; de uitgifte pakt dan de
                eerstvolgende die past.
              </p>
            )}
            {planMelding && (
              <p className={"plan-moment__melding" + (planMelding.fout ? " is-fout" : " is-goed")} role="status">
                {planMelding.tekst}
              </p>
            )}
          </div>

          <div className="quick-actions">
            <button className="primary-button full" onClick={opentNotitie}>
              <Plus size={17} /> Nieuwe notitie
            </button>
            <button className="outline-button full" onClick={sluitNazorg}>
              <CheckCircle2 size={17} /> Nazorg sluiten
            </button>
            {adoptant.telefoon && (
              <a className="outline-button full" href={`tel:${adoptant.telefoon}`}>
                <Phone size={17} /> Bel adoptant
              </a>
            )}
            {adoptant.email && (
              <a className="outline-button full" href={`mailto:${adoptant.email}`}>
                <Mail size={17} /> Email adoptant
              </a>
            )}
          </div>
        </div>
      </section>

      {infoDialog && (
        <div className="notitie-scrim" onClick={() => setInfoDialog(null)} role="presentation">
          <div
            className="notitie-dialog info-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={infoDialog === "entingen" ? "Vaccinatieoverzicht" : "Alle notities"}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {infoDialog === "entingen"
                ? `Vaccinatieoverzicht van ${dier.naam || "dit dier"}`
                : `Notities bij ${dier.naam || "dit dier"}`}
            </h3>
            <div className="info-dialog__body">
              {infoDialog === "entingen" ? (
                entingen.length === 0 ? (
                  <p className="small-text">Geen vaccinaties geregistreerd.</p>
                ) : (
                  <div className="data-lines">
                    {entingen.map((e, i) => (
                      <DataLine key={i} label={e.naam} date={e.datum} next={e.volgende} />
                    ))}
                  </div>
                )
              ) : notities.length === 0 ? (
                <p className="small-text">Nog geen notities.</p>
              ) : (
                notities.map((nt, i) => (
                  <TimelineNote
                    key={i}
                    color="green"
                    date={nt.datum || ""}
                    user={nt.melder || ""}
                    text={nt.tekst || ""}
                  />
                ))
              )}
            </div>
            <div className="notitie-dialog__acties">
              <button className="outline-button" onClick={() => setInfoDialog(null)}>
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {notitieOpen && (
        <div className="notitie-scrim" onClick={() => setNotitieOpen(false)} role="presentation">
          <div
            className="notitie-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Nieuwe notitie"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Nieuwe notitie bij {dier.naam || "dit dier"}</h3>
            <label className="notitie-veld">
              <span>Titel</span>
              <input
                type="text"
                placeholder="Titel (optioneel)"
                value={notitieTitel}
                onChange={(e) => setNotitieTitel(e.target.value)}
              />
            </label>
            <label className="notitie-veld">
              <span>Datum</span>
              <input
                type="date"
                value={notitieDatum}
                onChange={(e) => setNotitieDatum(e.target.value)}
              />
            </label>
            <label className="notitie-veld">
              <span>Notitie</span>
              <textarea
                rows={4}
                placeholder="Schrijf je notitie…"
                autoFocus
                value={notitieTekst}
                onChange={(e) => setNotitieTekst(e.target.value)}
              />
            </label>
            {(data.notitieTags || []).length > 0 && (
              <div className="notitie-veld">
                <span>Tags</span>
                <div className="notitie-tags">
                  {(data.notitieTags || []).map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className={"notitie-tag" + (notitieTags.includes(tag.id) ? " aan" : "")}
                      style={notitieTags.includes(tag.id) && tag.kleur ? { background: tag.kleur, borderColor: tag.kleur } : undefined}
                      onClick={() => wisselTag(tag.id)}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="notitie-veld">
              <span>Opvolgdatum (optioneel)</span>
              <input
                type="date"
                value={notitieOpvolg}
                min={notitieDatum}
                onChange={(e) => setNotitieOpvolg(e.target.value)}
              />
            </label>
            <label className="notitie-veld">
              <span>Auteur</span>
              <input
                type="text"
                value={notitieAuteur}
                onChange={(e) => setNotitieAuteur(e.target.value)}
              />
            </label>
            <label className="notitie-vast">
              <input
                type="checkbox"
                checked={notitieVast}
                onChange={(e) => setNotitieVast(e.target.checked)}
              />
              Vastgepind
            </label>
            <div className="notitie-dialog__acties">
              <button className="outline-button" onClick={() => setNotitieOpen(false)}>
                Annuleren
              </button>
              <button
                className="primary-button"
                disabled={!notitieTekst.trim()}
                onClick={notitieOpslaan}
              >
                <Plus size={16} /> Notitie opslaan
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ── Bouwstenen (uit de design-mockup) ────────────────────────────────────── */
type Icoon = typeof Smile;

function InfoCard({ icon: Icon, label, value, sub, tone }: {
  icon: Icoon;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: string;
}) {
  return (
    <div className="info-card">
      <Icon size={18} />
      <span>{label}</span>
      <strong className={tone || ""}>{value}</strong>
      <small>{sub}</small>
    </div>
  );
}

function PanelHeader({ number, title, action }: { number: string; title: string; action?: ReactNode }) {
  return (
    <div className="panel-header">
      <div>
        <span className="panel-number">{number}</span>
        <h2>{title}</h2>
      </div>
      {action || null}
    </div>
  );
}

function AnswerRow({ icon: Icon, question, answer, score, color }: {
  icon: Icoon;
  question: string | null;
  answer: string;
  score: string;
  color: string;
}) {
  // Lange open antwoorden passen niet in de drie kolommen: toon ze als tekstblok
  // onder de vraag, over de volle breedte. Drempel ~24 tekens: de langste nette
  // optie-labels ("Blaffen bij alleen zijn") blijven in de kolom, vrije tekst niet.
  const lang = (answer || "").length > 24;
  if (lang) {
    return (
      <div className="answer-row answer-row--lang">
        <div className="answer-row__top">
          <div className="question-cell">
            <Icon size={20} />
            <span>{question}</span>
          </div>
          <span className={`score-badge ${color}`}>{score}</span>
        </div>
        <div className="answer-row__tekst">{answer}</div>
      </div>
    );
  }
  return (
    <div className="answer-row">
      <div className="question-cell">
        <Icon size={20} />
        <span>{question}</span>
      </div>
      <strong>{answer}</strong>
      <span className={`score-badge ${color}`}>{score}</span>
    </div>
  );
}

function MedicalBlock({ title, badge, children }: { title: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <div className="medical-block">
      <div className="medical-block-title">
        <h3>{title}</h3>
        {badge}
      </div>
      {children}
    </div>
  );
}

function DataLine({ label, date, next }: { label: string | null; date: string | null; next?: string | null }) {
  return (
    <div className="data-line">
      <span>{label}</span>
      <span>{date || "—"}</span>
      <span>{next ? `Volgende: ${next}` : ""}</span>
    </div>
  );
}

function Medicine({ name, purpose, dose }: { name: string | null; purpose: string | null; dose: string | null }) {
  return (
    <div className="medicine">
      <div>
        <strong>{name}</strong>
        <small>{purpose}</small>
      </div>
      <span>{dose}</span>
      <span />
    </div>
  );
}

function TimelineNote({ color, date, user, text }: {
  color: string;
  date: string | null;
  user: string | null;
  text: string | null;
}) {
  return (
    <div className={`timeline-note ${color}`}>
      <div className="timeline-dot" />
      <div>
        <div className="note-meta">
          <span>{date}</span>
          <strong>{user}</strong>
        </div>
        <p>{text}</p>
      </div>
    </div>
  );
}

function Task({ icon: Icon, title, sub, date, status, color, actie }: {
  icon: Icoon;
  title: string | null;
  sub: string | null;
  date: string | null;
  status: string | null;
  color: string;
  /** Wat je met deze taak kunt doen — bij een belafspraak of huisbezoek het verslag schrijven. */
  actie?: ReactNode;
}) {
  return (
    <div className="task-row">
      <Icon size={20} />
      <div>
        <strong>{title}</strong>
        <small>{sub}</small>
        {actie}
      </div>
      <span>{date}</span>
      <span className={`score-badge ${color}`}>{status}</span>
    </div>
  );
}
